import { useEffect, useState } from 'react';

import { Button, CircularProgress, Link, Typography } from '@mui/material';
import {
  CheckIcon,
  ChevronDownDashboardIcon as ChevronDownIcon,
  ChevronUpIcon,
  colors,
  RadioUncheckedIcon,
} from '@truvhq/ui';
import { clsx } from 'clsx';
import { motion, useReducedMotion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';

import type { ChecklistStep, StepStatus } from 'entities/onboarding/types';
import { toInternalPath } from 'features/onboarding/lib/links';
import { toYouTubeEmbedUrl } from 'features/onboarding/lib/video';

/** The design's collapse curve, shared with the pane so header and rows open on one motion. */
export const COLLAPSE_TRANSITION = { duration: 0.24, ease: [0.22, 0.7, 0.3, 1] } as const;

type ChecklistStepRowProps = {
  step: ChecklistStep;
  blockerTitle: string | null;
  isExpanded: boolean;
  onExpand: () => void;
  onOpenBlocker: () => void;
  onSettle: (status: StepStatus) => Promise<unknown>;
  busy: StepStatus | null;
};

type StepActionsProps = {
  step: ChecklistStep;
  blockerTitle?: string | null;
  onOpenBlocker?: () => void;
  busy: StepStatus | null;
  onSettle: (status: StepStatus) => Promise<unknown>;
};

function StepMarker({ status }: { status: ChecklistStep['status'] }) {
  if (status === 'complete') {
    return (
      <div
        role="img"
        aria-label="Completed"
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success text-white"
      >
        <CheckIcon width={12} height={12} aria-hidden />
      </div>
    );
  }
  return (
    <RadioUncheckedIcon
      role="img"
      aria-label={status === 'skipped' ? 'Skipped' : 'Not done'}
      className={clsx('h-5 w-5 shrink-0', status === 'skipped' ? 'text-grey30' : 'text-grey40')}
    />
  );
}

// Routed client-side when the step's page lives inside the dashboard, so following it doesn't spawn
// a second copy of the app.
function StepAction({ link, title, isBlocked }: { link: string; title: string; isBlocked: boolean }) {
  const internalPath = toInternalPath(link);
  const sx = {
    borderRadius: 999,
    textTransform: 'none',
    px: 2.375,
    py: 1,
    boxShadow: 'none',
    '&.Mui-disabled': {
      backgroundColor: colors.primary.bright,
      color: 'rgba(255, 255, 255, 0.9)',
      cursor: 'not-allowed',
    },
  } as const;
  // Only one step is expanded at a time, so "Open" reads unambiguously on screen; the label names
  // the step for anyone who can't see which card it sits in.
  const label = `Open ${title}`;

  if (isBlocked) {
    return (
      <Button variant="contained" disabled aria-label={label} sx={sx}>
        Open
      </Button>
    );
  }
  if (!internalPath) {
    return (
      <Button variant="contained" href={link} target="_blank" rel="noopener noreferrer" aria-label={label} sx={sx}>
        Open
      </Button>
    );
  }
  return (
    <Button variant="contained" component={RouterLink} to={internalPath} aria-label={label} sx={sx}>
      Open
    </Button>
  );
}

/** Why a step can't be started yet, and the way to the step that unblocks it. */
export function StepGateNote({ blockerTitle, onOpenBlocker }: { blockerTitle: string; onOpenBlocker: () => void }) {
  return (
    <div className="mt-1.5 w-fit rounded-lg bg-grey20 px-[11px] py-2 text-grey60 text-xs leading-[17px]">
      Available after you{' '}
      <Link component="button" type="button" onClick={onOpenBlocker} className="font-medium text-xs">
        {blockerTitle.toLowerCase()}
      </Link>
    </div>
  );
}

const textActionSx = { minWidth: 76, px: 0.75, py: 0.875, fontSize: '0.875rem', textTransform: 'none' } as const;

/**
 * The step's own page, then the quieter way to tick it off by hand — or to take that back.
 *
 * A lock blocks the page — there is nothing to do there yet — but not the tick: someone who did the
 * work another way can still record it.
 */
export function ChecklistStepActions({ step, blockerTitle, onOpenBlocker, busy, onSettle }: StepActionsProps) {
  const isSettled = step.status === 'complete' || step.status === 'skipped';

  if (isSettled) {
    return (
      <div className="mt-3.5 flex flex-wrap items-center gap-2">
        <Button variant="text" sx={textActionSx} disabled={busy !== null} onClick={() => onSettle('pending')}>
          {busy === 'pending' ? <CircularProgress size={14} color="inherit" /> : 'Undo'}
        </Button>
      </div>
    );
  }

  return (
    <>
      {step.locked && blockerTitle && onOpenBlocker && (
        <StepGateNote blockerTitle={blockerTitle} onOpenBlocker={onOpenBlocker} />
      )}
      <div className="mt-3.5 flex flex-wrap items-center gap-2">
        {step.link && <StepAction link={step.link} title={step.title} isBlocked={Boolean(step.locked)} />}
        <Button variant="text" sx={textActionSx} disabled={busy !== null} onClick={() => onSettle('complete')}>
          {busy === 'complete' ? <CircularProgress size={14} color="inherit" /> : 'Mark done'}
        </Button>
      </div>
    </>
  );
}

export function ChecklistStepRow({
  step,
  blockerTitle,
  isExpanded,
  onExpand,
  onOpenBlocker,
  onSettle,
  busy,
}: ChecklistStepRowProps) {
  const isSettled = step.status === 'complete' || step.status === 'skipped';
  const embedUrl = toYouTubeEmbedUrl(step.video_url);
  const prefersReducedMotion = useReducedMotion();
  // The video mounts the first time the step opens and stays mounted: a step nobody opens never
  // loads YouTube, and collapsing doesn't pull the card's height out from under the animation.
  const [hasOpened, setHasOpened] = useState(isExpanded);

  useEffect(() => {
    if (isExpanded) {
      setHasOpened(true);
    }
  }, [isExpanded]);

  return (
    <li
      className={clsx('rounded-xl border bg-white px-3 py-2.5', isExpanded ? 'border-primary-bright' : 'border-grey30')}
    >
      <button
        type="button"
        onClick={onExpand}
        aria-expanded={isExpanded}
        className="flex w-full cursor-pointer items-start gap-3 bg-transparent p-0 text-left"
      >
        <StepMarker status={step.status} />
        <Typography
          className={clsx(
            'min-w-0 flex-1 text-sm',
            isSettled || step.locked ? 'text-grey60' : 'font-medium text-grey90',
          )}
        >
          {step.title}
        </Typography>
        {isExpanded ? (
          <ChevronUpIcon className="mt-1 h-4 w-4 shrink-0 text-grey60" aria-hidden />
        ) : (
          <ChevronDownIcon className="mt-1 h-4 w-4 shrink-0 text-grey60" aria-hidden />
        )}
      </button>

      {/* Nothing inside moves — the card just reveals what is already laid out. Hidden once closed,
          so the controls in here leave the tab order. */}
      <motion.div
        className="overflow-hidden"
        initial={false}
        animate={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
        transition={prefersReducedMotion ? { duration: 0 } : COLLAPSE_TRANSITION}
        aria-hidden={!isExpanded}
      >
        <div className="pt-1 pl-8">
          {/* Company-level steps settle for everyone, so a teammate's action would otherwise look
              like the step ticked itself off. */}
          {isSettled && step.completed_by && (
            <Typography variant="body2" className="text-grey60 text-xs">
              {step.status === 'skipped' ? 'Skipped' : 'Completed'} by {step.completed_by}
            </Typography>
          )}
          {step.description && (
            <Typography variant="body2" className="text-grey70">
              {step.description}
            </Typography>
          )}
          {embedUrl && hasOpened && (
            <div className="relative mt-3 w-full overflow-hidden rounded-lg" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute inset-0 h-full w-full"
                src={embedUrl}
                title={`${step.title} walkthrough`}
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          <ChecklistStepActions
            step={step}
            blockerTitle={blockerTitle}
            onOpenBlocker={onOpenBlocker}
            busy={busy}
            onSettle={onSettle}
          />
        </div>
      </motion.div>
    </li>
  );
}
